
import React, { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MapPin, Calendar, MessageSquare, Eye, User, Phone, Mail, Trash2, XCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "@/hooks/use-toast";

const STATUS_COLORS = {
  pending: "bg-yellow-100 text-yellow-800",
  approved: "bg-green-100 text-green-800",
  rejected: "bg-red-100 text-red-800",
};

const STATUS_LABELS: Record<string, string> = {
  pending: "معلق",
  approved: "مقبول",
  rejected: "مرفوض"
};

interface RentalRequest {
  id: string;
  message: string;
  status: string;
  created_at: string;
  tenant_name: string;
  tenant_phone: string;
  tenant_email: string;
  properties: {
    id: string;
    title: string;
    address: string;
    type: string;
    monthly_price: number;
    occupied: boolean;
  };
}

const RentalRequests = () => {
  const [requests, setRequests] = useState<RentalRequest[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setRequests([]);
        setLoading(false);
        return;
      }
      
      console.log('جلب طلبات الإيجار للمستخدم:', user.id);
      
      const { data, error } = await supabase
        .from("rental_requests")
        .select(`
          id,
          message,
          status,
          created_at,
          tenant_name,
          tenant_phone,
          tenant_email,
          properties (
            id,
            title,
            address,
            type,
            monthly_price,
            occupied
          )
        `)
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });
        
      if (error) {
        console.error('خطأ في جلب طلبات الإيجار:', error);
        setRequests([]);
      } else {
        console.log('تم جلب طلبات الإيجار:', data);
        setRequests(data || []);
      }
    } catch (error) {
      console.error('خطأ:', error);
      setRequests([]);
    }
    setLoading(false);
  };

  const deleteRequest = async (requestId: string) => {
    if (!confirm('هل أنت متأكد من حذف هذا الطلب؟')) return;

    try {
      console.log('حذف طلب الإيجار:', requestId);

      const { error } = await supabase
        .from('rental_requests')
        .delete()
        .eq('id', requestId);

      if (error) throw error;

      setRequests(prev => prev.filter(req => req.id !== requestId));

      toast({
        title: "تم الحذف",
        description: "تم حذف طلب الإيجار بنجاح",
      });

    } catch (error) {
      console.error('خطأ في حذف طلب الإيجار:', error);
      toast({
        title: "خطأ",
        description: "حدث خطأ أثناء حذف طلب الإيجار",
        variant: "destructive",
      });
    }
  };

  const cancelRequest = async (requestId: string) => {
    if (!confirm('هل أنت متأكد من إلغاء هذا الطلب؟')) return;

    try {
      console.log('إلغاء طلب الإيجار:', requestId);

      const { error } = await supabase
        .from('rental_requests')
        .update({ status: 'cancelled' })
        .eq('id', requestId);

      if (error) throw error;

      setRequests(prev =>
        prev.map(req =>
          req.id === requestId ? { ...req, status: 'cancelled' } : req
        )
      );

      toast({
        title: "تم الإلغاء",
        description: "تم إلغاء طلب الإيجار بنجاح",
      });

    } catch (error) {
      console.error('خطأ في إلغاء طلب الإيجار:', error);
      toast({
        title: "خطأ",
        description: "حدث خطأ أثناء إلغاء طلب الإيجار",
        variant: "destructive",
      });
    }
  };

  if (loading) return <div className="text-center py-10">جار التحميل...</div>;

  return (
    <div>
      {requests.length === 0 ? (
        <div className="text-center py-10 text-gray-600">
          <div className="mb-4">
            <MessageSquare className="h-16 w-16 mx-auto text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold mb-2">لا توجد طلبات إيجار</h3>
          <p className="text-gray-500 mb-4">ابدأ بتصفح العقارات وإرسال طلبات الإيجار</p>
          <Link to="/properties">
            <Button>تصفح العقارات</Button>
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold">طلبات الإيجار ({requests.length})</h2>
          </div>

          {requests.map(req => {
            const property = req.properties;
            if (!property) return null;

            return (
              <Card key={req.id} className="overflow-hidden">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg text-right">
                        {property.title}
                      </CardTitle>
                      <div className="flex items-center gap-1 text-gray-600 text-sm mt-1">
                        <MapPin className="h-3 w-3" />
                        <span>{property.address}</span>
                      </div>
                    </div>
                    <div className="text-left ml-4">
                      <div className="text-lg font-bold text-green-600">
                        {property.monthly_price} ر.ع
                      </div>
                      <div className="text-xs text-gray-500">شهرياً</div>
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent>
                  <div className="flex flex-wrap gap-2 mb-3">
                    <Badge variant="outline">{property.type}</Badge>
                    <Badge 
                      className={STATUS_COLORS[req.status as keyof typeof STATUS_COLORS] || "bg-gray-100 text-gray-800"}
                    >
                      {STATUS_LABELS[req.status] || req.status}
                    </Badge>
                    <Badge variant={property.occupied ? "secondary" : "default"}>
                      {property.occupied ? "مؤجر" : "متاح"}
                    </Badge>
                  </div>

                  {/* معلومات المتقدم */}
                  <div className="bg-blue-50 p-3 rounded-lg mb-3">
                    <h5 className="font-medium mb-2 text-blue-800">معلوماتك المرسلة:</h5>
                    <div className="space-y-1 text-sm">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-blue-600" />
                        <span>{req.tenant_name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-blue-600" />
                        <span>{req.tenant_phone}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-blue-600" />
                        <span>{req.tenant_email}</span>
                      </div>
                    </div>
                  </div>

                  {req.message && (
                    <div className="bg-gray-50 p-3 rounded-lg mb-3">
                      <div className="flex items-center gap-2 mb-1">
                        <MessageSquare className="h-4 w-4 text-gray-600" />
                        <span className="font-medium text-sm text-gray-800">رسالتك:</span>
                      </div>
                      <p className="text-gray-700 text-sm text-right">{req.message}</p>
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1 text-gray-500 text-sm">
                      <Calendar className="h-4 w-4" />
                      <span>تم الإرسال في: {new Intl.DateTimeFormat('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      }).format(new Date(req.created_at))}</span>
                    </div>
                    
                    <div className="flex gap-2">
                      <Link to={`/property/${property.id}`}>
                        <Button variant="outline" size="sm">
                          <Eye className="h-4 w-4 mr-1" />
                          عرض العقار
                        </Button>
                      </Link>
                      
                      {req.status === 'pending' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => cancelRequest(req.id)}
                          className="text-orange-600 hover:text-orange-700"
                        >
                          <XCircle className="h-4 w-4 mr-1" />
                          إلغاء الطلب
                        </Button>
                      )}
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => deleteRequest(req.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        حذف الطلب
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RentalRequests;
